"use client"

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'

import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet"
import { Button } from "@/components/ui/Button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import LoginForm from '@/components/sheets/LoginForm'
import RegisterForm from '@/components/sheets/RegisterForm'
import { useAuthSheet } from '@/context/AuthSheetContext'
import { useRouter } from 'next/navigation'
import { MemberProfile } from '@telegamez/types'
import { sanitizeRedirect } from '@/lib/redirect'
import { SITE_NAVIGATION } from '@/lib/site-navigation'

import { Menu as HamburgerIcon, LogIn, UserPlus } from 'lucide-react'
import { TvAppMenuSection } from '@/components/room/tv/TvAppMenuSection';
import { SubmitGameMenuSection } from '@/components/web-games/SubmitGameMenuSection';

interface HeaderClientProps {
  isLoggedIn: boolean;
  userData: (MemberProfile & { email?: string }) | null;
  logoutAction: (formData: FormData) => Promise<void>;
  siteName: string;
  allowPublicSignup?: boolean;
}

export function HeaderClient({ isLoggedIn, userData, logoutAction, siteName, allowPublicSignup = false }: HeaderClientProps) {
  const [previousLoginState, setPreviousLoginState] = useState(isLoggedIn)
  const [isNavSheetOpen, setIsNavSheetOpen] = useState(false)
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login')
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null)
  const {
    isSheetOpen,
    setIsSheetOpen,
    openSheet,
    sheetRedirectTo,
    avatarUrl,
    setAvatarUrl,
    userName,
    setUserName,
    userEmail,
    setUserEmail
  } = useAuthSheet()
  const router = useRouter()
  const searchParams = useSearchParams()

  // Auto-open login sheet when login=true query param is present.
  // If a sanitized `redirect_to` is present, stash it so success handlers
  // can push the user to their original destination after auth.
  useEffect(() => {
    const shouldOpenLogin = searchParams.get('login') === 'true'
    if (shouldOpenLogin && !isLoggedIn && !isSheetOpen) {
      const nextRedirect = sanitizeRedirect(searchParams.get('redirect_to'))
      if (nextRedirect) setPendingRedirect(nextRedirect)
      openSheet('login')
      // Clean up the URL by removing the login/redirect params
      const url = new URL(window.location.href)
      url.searchParams.delete('login')
      url.searchParams.delete('message')
      url.searchParams.delete('returnUrl')
      url.searchParams.delete('redirect_to')
      window.history.replaceState({}, '', url.pathname + url.search)
    }
  }, [searchParams, isLoggedIn, isSheetOpen, openSheet])

  // Handle nav sheet change - only allow closing via this handler (opening is via toggle button)
  const _handleNavSheetChange = useCallback((open: boolean) => {
    if (!open) {
      setIsNavSheetOpen(false)
    }
    // Ignore open=true since we handle opening via toggle button
  }, [])

  // Handle right sheet change - only allow closing via this handler (opening is via toggle button)
  const _handleRightSheetChange = useCallback((open: boolean) => {
    if (!open) {
      setIsSheetOpen(false)
    }
    // Ignore open=true since we handle opening via toggle button
  }, [setIsSheetOpen])

  // Handle nav sheet close
  const handleNavSheetClose = useCallback(() => {
    setIsNavSheetOpen(false)
  }, [])

  // Toggle nav sheet open/close
  const _handleNavSheetToggle = useCallback(() => {
    setIsNavSheetOpen(prev => {
      if (!prev) {
        setIsSheetOpen(false) // Close right sheet when opening nav
      }
      return !prev
    })
  }, [setIsSheetOpen])

  // Toggle right sheet open/close
  const _handleRightSheetToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (isSheetOpen) {
      setIsSheetOpen(false)
    } else {
      setIsNavSheetOpen(false) // Close nav sheet first
      openSheet('login')
    }
  }, [isSheetOpen, setIsSheetOpen, openSheet])

  // Handle right sheet close
  const handleSheetClose = useCallback(() => {
    setIsSheetOpen(false)
  }, [setIsSheetOpen])

  // Auth mode toggle
  const handleAuthModeChange = useCallback((mode: 'login' | 'register', e?: React.MouseEvent) => {
    e?.preventDefault()
    setAuthMode(mode)
  }, [])

  // LoginForm callbacks
  const handleToggleForm = useCallback(() => {
    setAuthMode(prev => prev === 'login' ? 'register' : 'login')
  }, [])
  const handleLoginSuccess = useCallback(() => {
    setIsSheetOpen(false)
    if (pendingRedirect) {
      const target = pendingRedirect
      setPendingRedirect(null)
      router.push(target)
      return
    }
    router.refresh()
  }, [setIsSheetOpen, router, pendingRedirect])

  const handleRegisterSuccess = useCallback(() => {
    setIsSheetOpen(false)
    if (pendingRedirect) setPendingRedirect(null)
    // RegisterForm performs its own window.location navigation using
    // the redirectTo prop, so we just close the sheet here.
  }, [setIsSheetOpen, pendingRedirect])


  // Close sheet when user transitions between login states
  useEffect(() => {
    if (!previousLoginState && isLoggedIn && isSheetOpen) {
      // User just logged in/registered, close the auth sheet
      setIsSheetOpen(false)
    } else if (previousLoginState && !isLoggedIn && isSheetOpen) {
      // User just logged out, close the profile sheet
      setIsSheetOpen(false)
    }

    // Clear AuthSheetContext data on logout
    if (previousLoginState && !isLoggedIn) {
      setAvatarUrl('')
      setUserName('')
      setUserEmail('')
    }

    setPreviousLoginState(isLoggedIn)
  }, [isLoggedIn, previousLoginState, isSheetOpen, setIsSheetOpen, setAvatarUrl, setUserName, setUserEmail])

  // const SheetHeader = () => (
  //   <div className="flex flex-col space-y-4 mb-4">
  //     <SheetClose asChild>
  //       <Button variant="secondary" className="w-full" onClick={handleSheetClose}>
  //         Close
  //       </Button>
  //     </SheetClose>
  //   </div>
  // )

  const ProfileContent = ({ className = "" }) => (
    <div className={`grid items-start gap-4 ${className}`}>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center space-x-4">
          <Avatar className="h-12 w-12">
            <AvatarImage 
              src={userData?.avatar || avatarUrl || undefined} 
              alt={userData?.name || "@user"}
              crossOrigin="anonymous"
            />
            <AvatarFallback>{(userData?.name || userName || "")[0]?.toUpperCase() || ""}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <h4 className="font-medium text-[var(--input-text-color)]">{ userName || userData?.name || ""}</h4>
            <p className="text-sm text-[var(--input-text-color)] opacity-80">{userEmail || userData?.email || ""}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col space-y-4">
        <SheetClose asChild>
          <Link href="/account" className="ext-lg font-semibold text-[var(--text-color)] hover:text-[var(--input-text-color)]">
            Account Settings
          </Link>
        </SheetClose>
        <form action={logoutAction}>
          <button
            type="submit"
            className="ext-lg font-semibold text-[var(--text-color)] hover:text-[var(--input-text-color)] "
          >
            Log out
          </button>
        </form>
      </div>
    </div>
  )

  const AuthContent = () => (
    <div className="flex flex-col space-y-6">
      {allowPublicSignup && (
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={(e) => handleAuthModeChange("login", e)}
            className={`flex flex-col items-center space-y-2 p-4 rounded-lg transition-colors
              ${authMode === "login"
                ? "bg-[var(--form-bg-color)] text-[var(--input-text-color)]"
                : "text-[var(--text-primary-alpha)] hover:text-[var(--text-primary)] hover:bg-[var(--form-bg-color)]"}`}
          >
            <LogIn className="h-6 w-6" />
            <div className="text-center">
              <div className="font-medium">Login</div>
              <div className="text-xs">Have an account?</div>
            </div>
          </button>

          <button
            type="button"
            onClick={(e) => handleAuthModeChange("register", e)}
            className={`flex flex-col items-center space-y-2 p-4 rounded-lg transition-colors
              ${authMode === "register"
                ? "bg-[var(--form-bg-color)] text-[var(--input-text-color)]"
                : "text-[var(--text-primary-alpha)] hover:text-[var(--text-primary)] hover:bg-[var(--form-bg-color)]"}`}
          >
            <UserPlus className="h-6 w-6" />
            <div className="text-center">
              <div className="font-medium">Register</div>
              <div className="text-xs">No account?</div>
            </div>
          </button>
        </div>
      )}

      <div className="pt-4">
        {allowPublicSignup && authMode === "register" ? (
          <RegisterForm
            onToggleForm={() => setAuthMode("login")}
            onSuccess={handleRegisterSuccess}
            redirectTo={pendingRedirect ?? sheetRedirectTo ?? undefined}
          />
        ) : (
          <LoginForm
            onToggleForm={handleToggleForm}
            onSuccess={handleLoginSuccess}
            allowPublicSignup={allowPublicSignup}
            redirectTo={pendingRedirect ?? sheetRedirectTo ?? undefined}
          />
        )}
      </div>
    </div>
  )

  return (
    <header className="fixed top-0 left-0 right-0 md:relative md:top-auto md:left-auto md:right-auto z-40 w-full border-b border-[var(--input-border-color)] bg-[#18385d]/95 backdrop-blur shadow-[0_1px_4px_rgba(0,0,0,0.15)]">
      <div className="container flex h-14 max-w-full px-4">
        <div className="flex items-center">
          <Sheet open={isNavSheetOpen} onOpenChange={setIsNavSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                className="text-[var(--text-color)] hover:text-[var(--input-text-color)] bg-transparent hover:bg-transparent focus:ring-0 flex items-center justify-center"
                onClick={(e) => {
                  if (isNavSheetOpen) {
                    e.preventDefault()
                    setIsNavSheetOpen(false)
                  } else {
                    setIsSheetOpen(false) // Close right sheet
                  }
                }}
              >
                <HamburgerIcon style={{ width: '2.5rem', height: '2.5rem' }} />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              title=" "
              className="[&>button]:hidden w-auto min-w-[180px] max-w-[70vw] sm:max-w-[320px] overflow-y-auto Sheet-Background border border-[var(--input-border-color)] backdrop-blur supports-[backdrop-filter]:bg-background/80 top-14 bottom-14 h-auto"
            >
              <div className="flex flex-col space-y-4 mb-4">
                <SheetClose asChild>
                  <Button variant="secondary" className="self-start px-8 bg-[var(--secondary-color)] text-[var(--input-text-color)] hover:text-[var(--input-text-color)] hover:bg-[var(--secondary-color)] hover:opacity-80" onClick={handleNavSheetClose}>
                    Close
                  </Button>
                </SheetClose>
              </div>
              <nav className="flex flex-col space-y-4">
                {SITE_NAVIGATION.map(item => (
                  <SheetClose asChild key={item.href}>
                    <Link href={item.href} className="text-lg font-semibold text-[var(--text-color)] hover:text-[var(--input-text-color)]">{item.label}</Link>
                  </SheetClose>
                ))}
              </nav>
              <TvAppMenuSection />
              <SubmitGameMenuSection />
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex-1 flex justify-center items-center">
          <Link href="/" className="flex items-center space-x-2">
            <img src="/images/logo192.png" alt={siteName} className="h-12 w-12" />
            <span className="hidden font-bold sm:inline-block text-2xl text-[var(--text-color)]">{siteName}</span>
          </Link>
        </div>

        <div className="flex items-center justify-end">
          {!isLoggedIn ? (
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-18 w-18 rounded-full bg-transparent hover:bg-transparent"
                  onClick={(e) => {
                    if (isSheetOpen) {
                      e.preventDefault()
                      setIsSheetOpen(false)
                    } else {
                      setIsNavSheetOpen(false) // Close nav sheet
                    }
                  }}
                >
                  <LogIn
                    className="text-[var(--text-primary)] transition-colors hover:text-[var(--input-text-color)]"
                  />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                title=" "
                className="[&>button]:hidden w-auto min-w-[180px] max-w-[70vw] sm:max-w-[320px] sheet-backdrop border-[var(--input-border-color)] top-14 bottom-14 h-auto"
              >
                <AuthContent />
              </SheetContent>
            </Sheet>
          ) : (
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-12 w-12 rounded-full bg-transparent hover:bg-transparent"
                  onClick={(e) => {
                    if (isSheetOpen) {
                      e.preventDefault()
                      setIsSheetOpen(false)
                    } else {
                      setIsNavSheetOpen(false) // Close nav sheet
                    }
                  }}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={userData?.avatar || avatarUrl || undefined}
                      alt={userData?.name || "@user"}
                      crossOrigin="anonymous"
                    />
                    <AvatarFallback>{(userData?.name || userName || "")[0]?.toUpperCase() || ""}</AvatarFallback>
                  </Avatar>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                title=" "
                className="[&>button]:hidden w-auto min-w-[180px] max-w-[70vw] sm:max-w-[320px] Sheet-Background border border-[var(--input-border-color)] backdrop-blur supports-[backdrop-filter]:bg-background/80 top-14 bottom-14 h-auto"
              >
                <div className="flex flex-col space-y-4 mb-4">
                  <SheetClose asChild>
                    <Button variant="secondary" className="self-start px-8 bg-[var(--secondary-color)] text-[var(--input-text-color)] hover:text-[var(--input-text-color)] hover:bg-[var(--secondary-color)] hover:opacity-80" onClick={handleSheetClose}>
                      Close
                    </Button>
                  </SheetClose>
                </div>
                <div>
                  <ProfileContent className="text-[var(--text-color)]" />
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  )
}
